import type { Result } from "starfx";
import { call, Err, Ok, safe } from "starfx";

import i18n from "@app/i18n";
import { bimapResult, identityResult, resultOf } from "@app/statefx/helpers";

import { callValue } from "../helpers/callValue";
import { callReturn, safeLast } from "../helpers/fxhelpers";
import { checkFileExists } from "./file-sys-ops";

import type { Operation } from "starfx";
import type { TJGPropTypes, TKarma, TKarmaFs } from "@app/types/insider";
import type { TreadFullTree } from "@app/types/insider";
export const loadProcessedIssScript = function* (fileName: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const handledKarmaError = (error: Error) => {
    const newMessage = `Unable to load the inno setup preprocessed file.[loadProcessedIssScript][#412]`;

    return new Error(newMessage);
  };
  const notFound = (error: Error) => {
    return "file not found";
  };

  const script = yield* callReturn(() =>
    appModule.builder.execute("issdebug", fileName)
  );
  // guarded failure
  // return coalesceResult(resultOf(script), notFound, identityResult);
  // controlled failure
  return bimapResult(resultOf(script), handledKarmaError, identityResult);
};

export const getDelphiSessionProp = function* (propName: TJGPropTypes) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() => appModule.builder.getProp(propName));
  if ("error" in prop) {
    return null;
  }
  if ("value" in prop) {
    return prop.value;
  }
};

export const dumpPrecompilerInfo = function* () {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const pci = yield* safe(() => appModule.builder.precompile()) as Operation<
    Result<boolean>
  >;
  if ("error" in pci) {
    return new Error(pci.error.message);
  }
  if ("value" in pci) {
    return pci.value as boolean;
  }
};

export const loadSvnStatus = function* () {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  // const result = yield* safe(() =>appModule.builder.readVersionCtrl()); >> typescript hackery: >>
  const result = yield* safe(() =>
    (appModule.builder.readVersionCtrl as TreadFullTree)()
  );

  /* 
  ?: Item is not under version control.
  M: Item has been modified.
  A: Item will be added to version control.
  D: Item will be deleted.
  C: Item's changes conflict with changes received from the repository.
  U: Item was updated.
  G: Item's local changes are merged with changes received from the repository. 
  */
  const res = yield* callReturn(() => result);

  if (res && typeof res === "string") {
    const lines = res
      .split("\n")
      .filter((line) => {
        return (
          line.trim() !== "" &&
          ["M", "A", "D", "C", "U", "G"].includes(line.trim()[0])
        );
      })
      .map((line) => ({
        svnStatus: line.trim()[0],
        fileName: line.trim().slice(1).trim(),
      }))
      //keyBy fileName
      .reduce((acc, curr) => {
        return { ...acc, [curr.fileName]: curr.svnStatus };
      }, {});
    return lines;
  }

  return null;
};

export const readCmdOutput = function* (cmd: string, cwd: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => appModule.builder.readVersionCtrl(cmd, cwd));
  const res = yield* callReturn(() => result);
  if (res && typeof res === "string") {
    return res;
  }
  return null;
};

export const upload = function* (buildId: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => appModule.builder.upload(buildId));
  return result;
};

export const setAddWatch = function* (fileName: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => appModule.builder.addWatch(fileName));
  return result;
};

export const runMaker = function* (fileName, flag = null) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("SWSBuilder")
  ) as Operation<Result<TKarma>>;

  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const handledKarmaError = (error: Error) => {
    const newMessage = `Unable to get the collect code result.[makerCollect][#1224]`;

    return new Error(newMessage);
  };
  const notFound = (error: Error) => {
    return "file not found";
  };
  let script;
  if (flag === "collect") {
    script = yield* callReturn(() =>
      appModule.builder.execute("maker--collect", fileName)
    );
  } else {
    script = yield* callReturn(() =>
      appModule.builder.execute("maker", fileName)
    );
  }

  return bimapResult(resultOf(script), handledKarmaError, identityResult);
};

export const makerCollect = function* (ccode: string) {
  const isDone = yield* callReturn(() => runMaker(`${ccode}.inc`, "collect"));
  if (isDone) {
    if (isDone == "done: same_manifest") {
      // message that the manifest is the same
      return Ok("same_manifest");
    }
    if (isDone == "done: terminal command") {
      // message that the manifest is the same
      return Ok("new_manifest");
    }
    return Err(new Error("unknown error"));
  }
  return false;
};

export const openDelphiDirOfDownloadCode = function* (downloadCode: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("open_dir_of_dowcode", downloadCode)
  );
  return prop;
  // if ('error' in prop) {
  //   return null;
  // }
  // if ('value' in prop) {
  //   return prop.value;
  // }
};
// Define the type of the arguments passed to nmodEfacturaEcmd

export const readDirOfDownloadCode = function* (jsonRecord: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;

  if (process.env.REACT_APP_MODE === "REACT") {
    // return 'to implement nodejs version';
  }
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safeLast(() =>
    appModule.efactura.efcmd("read_dir_of_dowcode", jsonRecord)
  );
  return prop;
};

export const updateDirOfDownloadCode = function* (jsonRecord: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;

  if (process.env.REACT_APP_MODE === "REACT") {
    return "to implement nodejs version";
  }
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safeLast(() =>
    appModule.efactura.efcmd("bring_to_local", jsonRecord)
  );
  return prop;
};

export const spvPrepareSPVPdf = function* ({
  currentFile,
  currentRecord,
}: {
  currentFile: string;
  currentRecord: string;
}): Operation<string> {
  //find if file exists
  if (currentFile) {
    const fileIs = yield* callReturn(() => checkFileExists(currentFile));
    if (fileIs) {
      return currentFile;
    }
  }
  if (process.env.REACT_APP_MODE === "REACT") {
    return "to implement nodejs version";
  }
  // else download the file
  const delpiApp = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delpiApp,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("prepare_dir_of_record", currentRecord)
  );
  if ("error" in prop) {
    return null;
  }
  if ("value" in prop) {
    return prop.value; // pdf file path...
  }
};

export const openPdfBlobFromPath = function* (
  path: string,
  suggestedNme: string
) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;

  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("open_pdf_as_blob", path, suggestedNme)
  );
  if ("error" in prop) {
    return null;
  }
  if ("value" in prop) {
    return prop.value;
  }
};

export const parseXmlOfRecord = function* (jsonRecord: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("parse_xml_of_record", jsonRecord)
  );
  if ("error" in prop) {
    return null;
  }
  if ("value" in prop) {
    return prop.value;
  }
};

export const deleteDirectory = function* (dirPath: string) {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("fs")
  ) as Operation<Result<TKarmaFs>>;
  const fs = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"), //"Cannot load the delphi project module. Aborting.",
    "terminal"
  );
  const result = yield* safe(() => fs.rm(dirPath, true));
  return result;
};

export const deleteLocalDir = function* (
  jsonRecord: string
): Operation<Result<string>> {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("cleanup_localdir", jsonRecord)
  );
  return prop;
};

export const deleteServerFiles = function* (
  jsonRecord: string
): Operation<Result<string>> {
  if (process.env.REACT_APP_MODE === "REACT") {
    const delphiBuilder = yield* safe(() =>
      window.delphiRequire("Jurnal_Quick")
    ) as Operation<Result<TKarma>>;
    const appModule = callValue(
      delphiBuilder,
      i18n.t("cannot_load_delphi_project_module"),
      "terminal"
    );
    const prop = yield* safe(() =>
      appModule.efactura.efcmd("cleanup_serverfiles", jsonRecord)
    );
    return prop;
  }
};

export const process_unknown = function* (
  sqlDate: string
): Operation<Result<string>> {
  const delphiBuilder = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("process_unknown", sqlDate)
  );
  return prop;
};

export const spvReadFullTree = function* (
  jsonData: string
): Operation<Result<string>> {
  const delphiBuilder = yield* safe(() => window.delphiRequire("Jurnal_Quick"));
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("read_full_dirtree_of_base", jsonData)
  );
  return prop;
  //read_full_dirtree_of_base
};

type TInvoiceTreeParams = {
  CIFNORO: string;
  INVOICE: string;
};
export const spvReadInvoiceTree = function* (
  data: TInvoiceTreeParams
): Operation<Result<string>> {
  const delphiBuilder = yield* safe(() => window.delphiRequire("Jurnal_Quick"));
  const appModule = callValue(
    delphiBuilder,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd(
      "read_full_dirtree_of_base",
      `/${data.CIFNORO}/${data.INVOICE}`
    )
  );
  return prop;
  //read_full_dirtree_of_base
};

export const openFXMLDetailsForm = function* (jsonString: string) {
  const delphi = yield* safe(() => window.delphiRequire("Jurnal_Quick"));
  const appModule = callValue(
    delphi,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );
  const prop = yield* safe(() =>
    appModule.efactura.efcmd("open_fxml_details_form", jsonString)
  );
  return prop;
};
